import { formatDisplayDate } from '../../../../utils/voucherHelpers';
import { usePlanAccess } from '../../../../components/gating/PlanGate';
import VoucherLastPageImg from '../../../../assets/images/Voucherlast page/VouhcerLastPage.png';
import GooglePlayBadge from '../../../../assets/images/google_play_badge.png';
import AppStoreBadge from '../../../../assets/images/app_store_badge.png';

const MEAL_PLAN_MAPPING = {
    "EPAI": "Room Only",
    "CPAI": "Breakfast",
    "MAPAI": "Breakfast & Dinner",
    "APAI": "All Meals",
    "EP": "Room Only",
    "CP": "Breakfast",
    "MAP": "Breakfast & Dinner",
    "AP": "All Meals"
};

const getCleanTerminalDisplay = (term, mode) => {
    if (!term) return null;
    const str = String(term).trim();
    if (!str) return null;
    if (/airport|national|international|aerodrome/i.test(str)) {
        const match = str.match(/\b(?:terminal|t)[-\s]*([0-9]+[a-z]?)\b/i) || str.match(/\bT([0-9]+)\b/i);
        if (!match) return null;
        return mode === 'train' ? `PF ${match[1].toUpperCase()}` : `Terminal ${match[1].toUpperCase()}`;
    }
    const matchNum = str.match(/^(?:terminal|t|pf|platform)?[-\s]*([0-9]+[a-z]?)$/i);
    if (matchNum) {
        return mode === 'train' ? `PF ${matchNum[1].toUpperCase()}` : `Terminal ${matchNum[1].toUpperCase()}`;
    }
    const digitMatch = str.match(/\b([0-9]+[a-z]?)\b/i);
    if (digitMatch && str.length <= 6) {
        return mode === 'train' ? `PF ${digitMatch[1].toUpperCase()}` : `Terminal ${digitMatch[1].toUpperCase()}`;
    }
    return null;
};

const pdfStyles = `
  .pdf-section { page-break-inside: avoid; margin-bottom: 25px; break-inside: avoid !important }
  .pdf-page-break { page-before: always; }
  .pdf-no-break { page-break-inside: avoid; break-inside: avoid !important }
  .pdf-container { padding: 30px 25px; line-height: 1.4; color: #1a1a1a; }
  .travel-booking-item { page-break-inside: avoid !important; break-inside: avoid !important; margin-bottom: 15px; }
  .itinerary-item { page-break-inside: avoid !important; break-inside: avoid !important; margin-bottom: 15px; }
  .pdf-header-bar { height: 65px; min-height: 65px; background-color: #f9fafb; border: 1px solid #0d9488; display: table; width: 100%; border-collapse: collapse; table-layout: fixed; }
  .pdf-header-item { display: table-cell; overflow: hidden; vertical-align: middle; text-align: center; width: 50%; }
  .pdf-header-text { display: inline-block; line-height: 1; margin: 0; padding: 0; text-align: left; }
`;

const PDFVoucherView = ({
    data,
    bookingId,
    logo,
    agentName,
    agentEmail,
    agentAddress,
    city,
    pincode,
    agentPhone,
    customTheme,
}) => {
    const { hasAccess: hasGrowthAccess } = usePlanAccess('growth');
    const effectiveTheme = customTheme || data?.voucher_theme;

    const displayBookingId = (bookingId || data?.bookingId || data?.booking_id || data?.voucher_id || 'a1b2c').toString().toLowerCase();
    const displayAgencyName = agentName || data?.agentName || data?.agencyName || 'Your Travel Partner';

    // An existing saved voucher has a database _id or createdAt timestamp
    const isExistingVoucher = Boolean(data?._id || data?.createdAt);

    // For existing vouchers from DB: preserve what was already saved.
    // For new vouchers in creation / templates: only serve custom theme if user has Growth plan access.
    const isCustom = isExistingVoucher
        ? Boolean(effectiveTheme?.is_custom || effectiveTheme?.isCustom || effectiveTheme?.enabled)
        : (hasGrowthAccess && Boolean(effectiveTheme?.is_custom || effectiveTheme?.isCustom || effectiveTheme?.enabled));

    const primaryColor = isCustom ? (effectiveTheme?.primary_color || effectiveTheme?.primaryColor || '#0d9488') : '#0d9488';
    const secondaryColor = isCustom ? (effectiveTheme?.secondary_color || effectiveTheme?.secondaryColor || '#1d4ed8') : '#1d4ed8';
    const textColor = isCustom ? (effectiveTheme?.text_color || effectiveTheme?.textColor || effectiveTheme?.info_color || effectiveTheme?.infoColor || '#111827') : '#111827';
    const headerBgColor = isCustom ? (effectiveTheme?.header_bg_color || effectiveTheme?.headerBgColor || '#ffffff') : '#ffffff';

    // Explicitly check boolean on customTheme first, then fallback to data.voucher_theme
    const rawIncludeItinerary = Boolean(customTheme?.include_itinerary ?? data?.voucher_theme?.include_itinerary ?? false);

    // Preserve saved itinerary inclusion for existing vouchers; only enforce Growth plan lock on new voucher generation
    const includeItinerary = isExistingVoucher ? rawIncludeItinerary : (hasGrowthAccess ? rawIncludeItinerary : false);
    const textStyle = isCustom ? { color: textColor } : undefined;

    const headerBarStyle = isCustom
        ? { height: '65px', minHeight: '65px', backgroundColor: '#f9fafb', border: `1px solid ${primaryColor}`, display: 'table', width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }
        : undefined;

    return (
        <div className="w-full bg-white font-sans" style={{ boxSizing: 'border-box' }}>
            <style>{pdfStyles}</style>

            <div
                data-pdf-section="header"
                className="pdf-no-break w-full"
                style={{
                    backgroundColor: headerBgColor || '#ffffff',
                    padding: '40px 36px 20px 36px',
                    borderBottom: `2px solid ${primaryColor}`,
                    boxSizing: 'border-box'
                }}
            >
                <div className="flex items-end justify-between">
                    <div className="flex flex-col items-start">
                        {logo && (
                            <img src={logo} alt="Agency Logo" className="h-16 w-auto max-w-[240px] object-contain mb-4 shadow-sm" />
                        )}
                        <h2 className={`text-3xl font-black ${!isCustom ? 'text-black' : ''} uppercase tracking-tight leading-tight mb-1`} style={textStyle}>{agentName || 'Agent Name'}</h2>
                        <div className="text-[10px] text-gray-600 max-w-[250px] leading-relaxed">
                            <p
                                className={`font-bold ${!isCustom ? 'text-teal-700' : ''} uppercase tracking-widest text-[8px] mb-1`}
                                style={isCustom ? { color: primaryColor } : undefined}
                            >
                                Office Address
                            </p>
                            <p className={`font-medium ${!isCustom ? 'text-gray-700' : ''}`} style={textStyle}>
                                {agentAddress}
                                {city ? `, ${city}` : ''}
                                {pincode ? ` - ${pincode}` : ''}
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-col justify-end text-right">
                        <p
                            className={`text-[10px] font-bold ${!isCustom ? 'text-teal-700 border-teal-500' : ''} uppercase tracking-widest border-b-2 pb-1 mb-3 inline-block`}
                            style={isCustom ? { color: primaryColor, borderColor: primaryColor } : undefined}
                        >
                            Contact details
                        </p>
                        <div className="space-y-1">
                            <p className={`text-[11px] font-bold ${!isCustom ? 'text-gray-900' : ''}`} style={textStyle}>
                                <span
                                    className={`${!isCustom ? 'text-teal-600' : ''} uppercase text-[8px] mr-2`}
                                    style={isCustom ? { color: primaryColor } : undefined}
                                >
                                    Email:
                                </span> {agentEmail || 'N/A'}
                            </p>
                            {agentPhone && (
                                <p className={`text-[11px] font-bold ${!isCustom ? 'text-gray-900' : ''}`} style={textStyle}>
                                    <span
                                        className={`${!isCustom ? 'text-teal-600' : ''} uppercase text-[8px] mr-2`}
                                        style={isCustom ? { color: primaryColor } : undefined}
                                    >
                                        Phone:
                                    </span> {agentPhone}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="w-full" style={{ padding: '24px 36px 40px 36px', boxSizing: 'border-box' }}>
                <div data-pdf-section="title" className="text-center mb-6">
                    <h1 className={`text-3xl font-normal ${!isCustom ? 'text-gray-700' : ''} uppercase tracking-widest`} style={textStyle}>Travel Confirmation Voucher</h1>
                </div>

                <div
                    data-pdf-section="booking-bar"
                    className="pdf-header-bar mb-6"
                    style={headerBarStyle}
                >
                    <div className="pdf-header-item" style={{ width: '100%' }}>
                        <div className="pdf-header-text">
                            <span
                                className={`font-bold ${!isCustom ? 'text-teal-700' : ''} uppercase tracking-widest text-[10px] mr-2`}
                                style={isCustom ? { color: primaryColor } : undefined}
                            >
                                Booking ID:
                            </span>
                            <span className={`font-bold ${!isCustom ? 'text-gray-900' : ''} text-[20px] leading-none`} style={textStyle}>{bookingId?.toLowerCase()}</span>
                        </div>
                    </div>
                </div>

                <div data-pdf-section="guest-info" className="mb-6 pl-2">
                    <h3
                        className={`text-2xl font-bold ${!isCustom ? 'text-blue-700 border-blue-50' : ''} uppercase mb-4 border-b-4 pb-2`}
                        style={isCustom ? { color: secondaryColor, borderColor: `${secondaryColor}15` } : undefined}
                    >
                        Guest Information
                    </h3>
                    <div className="grid grid-cols-3 gap-8 mb-6">
                        <div>
                            <p className="text-xs text-gray-500 mb-1">Guest Name</p>
                            <p className={`text-lg font-bold ${!isCustom ? 'text-gray-900' : ''}`} style={textStyle}>{data.guest_information?.guest_name || 'N/A'}</p>
                        </div>
                        <div>
                            <p className="text-xs text-gray-500 mb-1">Adult</p>
                            <p className={`text-lg font-bold ${!isCustom ? 'text-gray-900' : ''}`} style={textStyle}>{data.guest_information?.total_guests_adults || 0}</p>
                        </div>
                        <div>
                            <p className="text-xs text-gray-500 mb-1">Children</p>
                            <p className={`text-lg font-bold ${!isCustom ? 'text-gray-900' : ''}`} style={textStyle}>{data.guest_information?.total_guests_children || 0}</p>
                        </div>
                    </div>
                    <h3
                        className={`text-2xl font-bold ${!isCustom ? 'text-blue-700' : ''} uppercase mb-4`}
                        style={isCustom ? { color: secondaryColor } : undefined}
                    >
                        Tour Information
                    </h3>
                    <div className="grid grid-cols-2 gap-8">
                        <div>
                            <p className="text-xs text-gray-500 mb-1">Start Date</p>
                            <p className={`text-lg font-bold ${!isCustom ? 'text-gray-900' : ''}`} style={textStyle}>{formatDisplayDate(data.tour_info?.start_date) || 'N/A'}</p>
                        </div>
                        <div>
                            <p className="text-xs text-gray-500 mb-1">End Date</p>
                            <p className={`text-lg font-bold ${!isCustom ? 'text-gray-900' : ''}`} style={textStyle}>{formatDisplayDate(data.tour_info?.end_date) || 'N/A'}</p>
                        </div>
                    </div>
                </div>

                <div className="border-t border-gray-200 my-4"></div>

                {data.hotel_bookings && data.hotel_bookings.length > 0 && (
                    <div data-pdf-section="hotel-bookings-card" className="mb-6">
                        <h3
                            data-pdf-section="hotels-heading"
                            className={`text-2xl font-bold ${!isCustom ? 'text-blue-700 border-blue-50' : ''} uppercase mb-4 border-b-4 pb-2`}
                            style={isCustom ? { color: secondaryColor, borderColor: `${secondaryColor}15` } : undefined}
                        >
                            Hotel Bookings
                        </h3>
                        <div className="space-y-4">
                            {data.hotel_bookings.map((hotel, idx) => (
                                <div
                                    key={idx}
                                    data-pdf-section={`hotel-${idx}`}
                                    className={`pt-2 mb-6 pb-4 border-b-2 ${!isCustom ? 'border-teal-500' : ''} last:border-0 last:mb-0 last:pb-0`}
                                    style={isCustom ? { borderColor: `${primaryColor}60` } : undefined}
                                >
                                    <h3 className={`text-lg font-bold ${!isCustom ? 'text-gray-800' : ''} mb-2 pb-1 truncate`} style={textStyle}>
                                        {hotel.hotel_name || 'Hotel Name Not Available'} {hotel.city ? `(${hotel.city})` : ''}
                                    </h3>
                                    {(hotel.hotel_address || hotel.hotel_phone) && (
                                        <p className={`text-xs ${!isCustom ? 'text-gray-500' : ''} mb-4`} style={textStyle}>
                                            {hotel.hotel_address}{hotel.hotel_address && hotel.hotel_phone ? ' · ' : ''}{hotel.hotel_phone ? `Phone: ${hotel.hotel_phone}` : ''}
                                        </p>
                                    )}
                                    {hotel.confirmation_number && hotel.confirmation_number.trim() && hotel.confirmation_number.toLowerCase() !== 'pending' && (
                                        <div className="mb-4">
                                            <span className="text-sm font-bold text-gray-700 uppercase tracking-widest text-[10px]">Confirmation Number: </span>
                                            <span className={`text-sm ${!isCustom ? 'text-gray-600' : ''} font-mono`} style={textStyle}>{hotel.confirmation_number}</span>
                                        </div>
                                    )}
                                    <div className="grid grid-cols-3 gap-4 border-t border-gray-50 pt-4">
                                        <div><p className="text-[10px] text-gray-500 uppercase font-black tracking-widest mb-1">Check-in</p><p className={`font-bold text-sm ${!isCustom ? 'text-gray-800' : ''}`} style={textStyle}>{formatDisplayDate(hotel.check_in_date) || 'N/A'}{hotel.check_in_time ? ` · ${hotel.check_in_time}` : ''}</p></div>
                                        <div><p className="text-[10px] text-gray-500 uppercase font-black tracking-widest mb-1">Check-out</p><p className={`font-bold text-sm ${!isCustom ? 'text-gray-800' : ''}`} style={textStyle}>{formatDisplayDate(hotel.check_out_date) || 'N/A'}{hotel.check_out_time ? ` · ${hotel.check_out_time}` : ''}</p></div>
                                        <div><p className="text-[10px] text-gray-500 uppercase font-black tracking-widest mb-1">Meal Plan</p><p className={`font-bold text-sm ${!isCustom ? 'text-gray-800' : ''}`} style={textStyle}>{MEAL_PLAN_MAPPING[hotel.meal_plan?.toUpperCase()] || hotel.meal_plan || 'N/A'}</p></div>
                                    </div>
                                    <div className="grid grid-cols-3 gap-4 mt-4">
                                        <div><p className="text-[10px] text-gray-500 uppercase font-black tracking-widest mb-1">Category</p><p className={`font-bold text-sm ${!isCustom ? 'text-gray-800' : ''}`} style={textStyle}>{hotel.room_category || 'Standard'}</p></div>
                                        <div><p className="text-[10px] text-gray-500 uppercase font-black tracking-widest mb-1">Rooms</p><p className={`font-bold text-sm ${!isCustom ? 'text-gray-800' : ''}`} style={textStyle}>{hotel.no_of_rooms || 1}</p></div>
                                        <div><p className="text-[10px] text-gray-500 uppercase font-black tracking-widest mb-1">Nights</p><p className={`font-bold text-sm ${!isCustom ? 'text-gray-800' : ''}`} style={textStyle}>{hotel.no_of_nights || '-'}</p></div>
                                    </div>
                                    {hotel.inclusions && (
                                        <div className="mt-3.5 pt-3 border-t border-gray-50">
                                            <p className="text-[10px] text-gray-500 uppercase font-black tracking-widest mb-1">Inclusions</p>
                                            <p className={`font-semibold text-xs leading-relaxed ${!isCustom ? 'text-gray-700' : ''}`} style={textStyle}>{hotel.inclusions}</p>
                                        </div>
                                    )}
                                    {hotel.custom_fields && hotel.custom_fields.length > 0 && (
                                        <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-dashed border-gray-100">
                                            {hotel.custom_fields.map((field, fIdx) => (
                                                <div key={fIdx}>
                                                    <p className="text-[10px] text-gray-500 uppercase font-black tracking-widest mb-1">{field.label || 'Field'}</p>
                                                    <p className={`font-bold text-sm ${!isCustom ? 'text-gray-800' : ''}`} style={textStyle}>{field.value || '-'}</p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {data.travel_bookings && data.travel_bookings.length > 0 && (
                    <div data-pdf-section="travel-bookings-card" className="mb-6">
                        <h3
                            data-pdf-section="travels-heading"
                            className={`text-2xl font-bold ${!isCustom ? 'text-blue-700 border-blue-50' : ''} uppercase mb-4 border-b-4 pb-2`}
                            style={isCustom ? { color: secondaryColor, borderColor: `${secondaryColor}15` } : undefined}
                        >
                            Travel Bookings
                        </h3>
                        <div className="space-y-4">
                            {data.travel_bookings.map((travel, idx) => (
                                <div
                                    key={idx}
                                    data-pdf-section={`travel-${idx}`}
                                    className={`pt-2 mb-12 pb-8 border-b-2 ${!isCustom ? 'border-teal-500' : ''} last:border-0 last:mb-0 last:pb-0 relative`}
                                    style={isCustom ? { borderColor: `${primaryColor}60` } : undefined}
                                >
                                    <div className="grid grid-cols-2 gap-8">
                                        <div className="text-center space-y-6">
                                            <div>
                                                <p
                                                    className={`text-xs ${!isCustom ? 'text-blue-900' : ''} font-bold uppercase tracking-wider mb-1`}
                                                    style={isCustom ? { color: secondaryColor } : undefined}
                                                >
                                                    Operator
                                                </p>
                                                <p className={`text-lg font-semibold ${!isCustom ? 'text-gray-800' : ''} leading-tight`} style={textStyle}>{travel.operator || 'N/A'}</p>
                                            </div>
                                            <div>
                                                <p
                                                    className={`text-xs ${!isCustom ? 'text-blue-900' : ''} font-bold uppercase tracking-wider mb-1`}
                                                    style={isCustom ? { color: secondaryColor } : undefined}
                                                >
                                                    Mode of Transport
                                                </p>
                                                <p className={`text-lg font-semibold ${!isCustom ? 'text-gray-800' : ''} capitalize leading-tight`} style={textStyle}>{travel.mode_of_transport}</p>
                                            </div>
                                            <div className="space-y-1">
                                                <p className="text-xs text-gray-500 uppercase">From</p>
                                                <p className={`text-md font-bold ${!isCustom ? 'text-gray-900' : ''} leading-tight`} style={textStyle}>
                                                    {travel.from}
                                                    {getCleanTerminalDisplay(travel.departure_terminal, travel.mode_of_transport) && (
                                                        <span className="text-xs font-semibold text-gray-500 ml-1.5 font-sans">
                                                            ({getCleanTerminalDisplay(travel.departure_terminal, travel.mode_of_transport)})
                                                        </span>
                                                    )}
                                                </p>
                                                <p className="text-xs text-gray-500 uppercase pt-2">To</p>
                                                <p className={`text-md font-bold ${!isCustom ? 'text-gray-900' : ''} leading-tight`} style={textStyle}>
                                                    {travel.to}
                                                    {getCleanTerminalDisplay(travel.arrival_terminal, travel.mode_of_transport) && (
                                                        <span className="text-xs font-semibold text-gray-500 ml-1.5 font-sans">
                                                            ({getCleanTerminalDisplay(travel.arrival_terminal, travel.mode_of_transport)})
                                                        </span>
                                                    )}
                                                </p>
                                            </div>
                                            <div>
                                                <p
                                                    className={`text-xs ${!isCustom ? 'text-blue-900' : ''} font-bold uppercase tracking-wider mb-1`}
                                                    style={isCustom ? { color: secondaryColor } : undefined}
                                                >
                                                    {travel.mode_of_transport === 'flight' ? 'Flight Number' :
                                                        travel.mode_of_transport === 'train' ? 'Train Number' :
                                                            travel.mode_of_transport === 'bus' ? 'Bus Number' :
                                                                travel.mode_of_transport === 'waterways' ? 'Vessel / Cruise Name' : 'Vehicle Number'}
                                                </p>
                                                <p className={`text-lg font-semibold ${!isCustom ? 'text-gray-800' : ''} leading-tight`} style={textStyle}>{travel.vehicle_number || '-'}</p>
                                            </div>
                                            {travel.driver_name && (
                                                <div>
                                                    <p
                                                        className={`text-xs ${!isCustom ? 'text-blue-900' : ''} font-bold uppercase tracking-wider mb-1`}
                                                        style={isCustom ? { color: secondaryColor } : undefined}
                                                    >
                                                        Driver Name
                                                    </p>
                                                    <p className={`text-lg font-semibold ${!isCustom ? 'text-gray-800' : ''} leading-tight`} style={textStyle}>{travel.driver_name}</p>
                                                </div>
                                            )}
                                            {travel.driver_contact && (
                                                <div>
                                                    <p
                                                        className={`text-xs ${!isCustom ? 'text-blue-900' : ''} font-bold uppercase tracking-wider mb-1`}
                                                        style={isCustom ? { color: secondaryColor } : undefined}
                                                    >
                                                        Driver Contact
                                                    </p>
                                                    <p className={`text-lg font-semibold ${!isCustom ? 'text-gray-800' : ''} leading-tight font-mono`} style={textStyle}>{travel.driver_contact}</p>
                                                </div>
                                            )}
                                            <div className="pt-4 border-t border-gray-100">
                                                <p
                                                    className={`text-sm font-bold ${!isCustom ? 'text-blue-900' : ''} mb-2`}
                                                    style={isCustom ? { color: secondaryColor } : undefined}
                                                >
                                                    Passenger Name
                                                </p>
                                                <div className={`text-sm ${!isCustom ? 'text-gray-700' : ''} space-y-1`} style={textStyle}>
                                                    {(travel.passenger_name || '').split(',').map((n, i) => (
                                                        <div key={i}>{n.trim()}</div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-center space-y-6">
                                            <div>
                                                <p
                                                    className={`text-xs ${!isCustom ? 'text-blue-900' : ''} font-bold uppercase tracking-wider mb-1`}
                                                    style={isCustom ? { color: secondaryColor } : undefined}
                                                >
                                                    Journey Date
                                                </p>
                                                <p className={`text-lg font-semibold ${!isCustom ? 'text-gray-800' : ''} leading-tight`} style={textStyle}>{formatDisplayDate(travel.journey_date)}</p>
                                            </div>
                                            <div>
                                                <p
                                                    className={`text-xs ${!isCustom ? 'text-blue-900' : ''} font-bold uppercase tracking-wider mb-1`}
                                                    style={isCustom ? { color: secondaryColor } : undefined}
                                                >
                                                    Departure Time
                                                </p>
                                                <p className={`text-lg font-semibold ${!isCustom ? 'text-gray-800' : ''} leading-tight`} style={textStyle}>{travel.departure_time || 'N/A'}</p>
                                            </div>
                                            <div>
                                                <p
                                                    className={`text-xs ${!isCustom ? 'text-blue-900' : ''} font-bold uppercase tracking-wider mb-1`}
                                                    style={isCustom ? { color: secondaryColor } : undefined}
                                                >
                                                    Arrival Time
                                                </p>
                                                <p className={`text-lg font-semibold ${!isCustom ? 'text-gray-800' : ''} leading-tight`} style={textStyle}>{travel.arrival_time || 'N/A'}</p>
                                            </div>
                                            {!['cab', 'taxi'].includes(travel.mode_of_transport?.toLowerCase()) && (
                                                <div>
                                                    <p
                                                        className={`text-xs ${!isCustom ? 'text-blue-900' : ''} font-bold uppercase tracking-wider mb-1`}
                                                        style={isCustom ? { color: secondaryColor } : undefined}
                                                    >
                                                        {travel.mode_of_transport === 'waterways' ? 'Ticket / PNR No.' : 'PNR'}
                                                    </p>
                                                    <p className={`text-lg font-bold ${!isCustom ? 'text-gray-800' : ''} font-mono leading-tight break-words`} style={textStyle}>{travel.pnr || '-'}</p>
                                                </div>
                                            )}
                                            {!['cab', 'taxi'].includes(travel.mode_of_transport?.toLowerCase()) && (
                                                <div className="pt-4 border-t border-gray-100">
                                                    <p
                                                        className={`text-sm font-bold ${!isCustom ? 'text-blue-900' : ''} mb-2`}
                                                        style={isCustom ? { color: secondaryColor } : undefined}
                                                    >
                                                        {travel.mode_of_transport === 'waterways' ? 'Seat / Cabin No.' : 'Seat Numbers'}
                                                    </p>
                                                    <p className={`text-sm ${!isCustom ? 'text-gray-700' : ''} leading-relaxed font-medium`} style={textStyle}>{travel.seat_no || '-'}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {travel.custom_fields && travel.custom_fields.length > 0 && (
                                        <div className="grid grid-cols-3 gap-4 mt-6 pt-4 border-t border-dashed border-gray-200">
                                            {travel.custom_fields.map((field, fIdx) => (
                                                <div key={fIdx}>
                                                    <p className="text-[10px] text-gray-500 uppercase font-black tracking-widest mb-1">{field.label || 'Field'}</p>
                                                    <p className={`font-bold text-sm ${!isCustom ? 'text-gray-800' : ''}`} style={textStyle}>{field.value || '-'}</p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Itinerary Section */}
                {includeItinerary && data?.itinerary && data.itinerary.length > 0 && data.itinerary.some(d => d.title || d.day || d.date || (d.events && d.events.some(e => (typeof e === 'object' ? e.title : e)?.trim())) || (d.remarks && d.remarks.some(r => r && r.trim())) || (d.pointers && d.pointers.some(p => p && p.trim()))) && (
                    <div data-pdf-section="itinerary" className="mb-6 pdf-section">
                        <h3
                            data-pdf-section="itinerary-heading"
                            className={`text-2xl font-bold ${!isCustom ? 'text-blue-700 border-blue-100' : ''} uppercase mb-4 border-b-4 pb-2`}
                            style={isCustom ? { color: secondaryColor, borderColor: `${secondaryColor}15` } : undefined}
                        >
                            Trip Itinerary
                        </h3>
                        <div className="space-y-4">
                            {data.itinerary.map((day, idx) => {
                                const dayTitle = day.day_title || day.title || (typeof day.day === 'string' && !day.day.startsWith('Day ') ? day.day : '') || `Day ${idx + 1}`;
                                const rawEvents = (Array.isArray(day.events) && day.events.length > 0)
                                    ? day.events.map(e => ({
                                        title: (typeof e === 'object' ? e.title : e) || '',
                                        time: (typeof e === 'object' ? e.time : null)
                                      }))
                                    : (Array.isArray(day.remarks) && day.remarks.length > 0)
                                        ? day.remarks.map((r, rIdx) => ({
                                            title: r || '',
                                            time: (Array.isArray(day.timings) && day.timings[rIdx]) ? (typeof day.timings[rIdx] === 'object' ? day.timings[rIdx].time : day.timings[rIdx]) : null
                                          }))
                                        : (Array.isArray(day.pointers) && day.pointers.length > 0)
                                            ? day.pointers.map(p => ({ title: p || '', time: null }))
                                            : (day.description ? [{ title: day.description, time: null }] : []);
                                const validEvents = rawEvents.filter(e => e.title && typeof e.title === 'string' && e.title.trim());

                                return (
                                    <div
                                        key={day.id || idx}
                                        data-pdf-section={`itin-day-${idx}`}
                                        className={`itinerary-item rounded-2xl border ${!isCustom ? 'border-slate-200/90 bg-slate-50/50' : ''} p-4 sm:p-5`}
                                        style={isCustom ? { borderColor: `${secondaryColor}25`, backgroundColor: `${secondaryColor}04` } : undefined}
                                    >
                                        {/* Day Header Row */}
                                        <div
                                            className={`flex justify-between items-center pb-2.5 mb-3 ${validEvents.length > 0 ? 'border-b border-slate-200' : ''}`}
                                            style={isCustom && validEvents.length > 0 ? { borderColor: `${secondaryColor}20` } : undefined}
                                        >
                                            <div className="flex items-center gap-2 min-w-0">
                                                <span
                                                    className={`font-black text-sm uppercase tracking-wider shrink-0 ${!isCustom ? 'text-blue-900' : ''}`}
                                                    style={isCustom ? { color: secondaryColor } : undefined}
                                                >
                                                    Day {typeof day.day === 'number' ? day.day : (idx + 1)}:
                                                </span>
                                                <span className={`text-base font-bold ${!isCustom ? 'text-gray-900' : ''}`} style={textStyle}>
                                                    {dayTitle}
                                                </span>
                                            </div>
                                            {day.date && (
                                                <span
                                                    className={`text-xs font-bold shrink-0 ${!isCustom ? 'text-gray-600' : ''}`}
                                                    style={isCustom ? { color: secondaryColor } : undefined}
                                                >
                                                    {formatDisplayDate(day.date)}
                                                </span>
                                            )}
                                        </div>

                                        {/* Pointers / Activities */}
                                        {validEvents.length > 0 && (
                                            <ul className="space-y-2 pl-1">
                                                {validEvents.map((evt, pIdx) => (
                                                    <li key={pIdx} className="flex gap-2.5 items-start text-xs sm:text-sm leading-relaxed">
                                                        <span
                                                            className={`font-bold text-xs mt-0.5 shrink-0 ${!isCustom ? 'text-blue-600' : ''}`}
                                                            style={isCustom ? { color: secondaryColor } : undefined}
                                                        >
                                                            ➤
                                                        </span>
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            {evt.time && (
                                                                <span
                                                                    className={`text-[11px] font-mono font-bold px-2 py-0.5 rounded-md ${!isCustom ? 'bg-blue-50 text-blue-800 border border-blue-200/60' : ''}`}
                                                                    style={isCustom ? { backgroundColor: `${secondaryColor}15`, color: secondaryColor, borderColor: `${secondaryColor}30` } : undefined}
                                                                >
                                                                    {evt.time}
                                                                </span>
                                                            )}
                                                            <span className={`${!isCustom ? 'text-gray-800' : ''}`} style={textStyle}>
                                                                {evt.title}
                                                            </span>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}


                <div className="text-center mb-6 px-12">
                    <p
                        className={`text-sm font-semibold ${!isCustom ? 'text-teal-700' : ''} italic`}
                        style={isCustom ? { color: primaryColor } : undefined}
                    >
                        "Your journey deserves the best. Download the app and explore restaurants, shopping, and many more."
                    </p>
                </div>

                <div data-pdf-section="notes" className="mb-6">
                    <h3
                        className={`text-2xl font-bold ${!isCustom ? 'text-blue-700 border-blue-100' : ''} uppercase mb-4 border-b-4 pb-2`}
                        style={isCustom ? { color: secondaryColor, borderColor: `${secondaryColor}15` } : undefined}
                    >
                        Important Notes
                    </h3>
                    <ul className="space-y-2">
                        {[
                            "Carry a valid photo ID (Aadhaar/Passport) at all times during the trip.",
                            "Personal expenses, tips & beverages are not included in the package.",
                            "Arrive at airport/station at least 2 hrs (domestic) or 3 hrs (international) before departure.",
                        ].map((note, idx) => (
                            <li key={idx} className={`flex gap-2 items-start text-sm ${!isCustom ? 'text-gray-700' : ''}`} style={textStyle}>
                                <span
                                    className={`${!isCustom ? 'text-blue-500' : ''} mt-1`}
                                    style={isCustom ? { color: secondaryColor } : undefined}
                                >
                                    ➤
                                </span>
                                <span style={textStyle}>{note}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div data-pdf-section="footer" className="mt-8 w-full border-t-2 border-gray-100 pt-6">
                    <div className="flex justify-between items-center text-left text-xs text-gray-400">
                        <p>Date: {formatDisplayDate(new Date().toISOString().split('T')[0])}</p>
                        <p className="text-[10px] italic">Thank you for choosing {displayAgencyName}</p>
                    </div>
                </div>

                {/* Voucher Last Page - App Promotion & Access Code */}
                <div
                    data-pdf-section="last-page"
                    className="pdf-page-break pdf-no-break w-full relative bg-[#067978] overflow-hidden select-none rounded-xl"
                    style={{ pageBreakBefore: 'always', breakBefore: 'always', marginTop: '36px' }}
                >
                    {/* Top HTML Banner Header */}
                    <div className="w-full bg-[#055b5a] text-white py-3 px-4 text-center border-b border-[#0891b2]/40 shadow-sm relative z-10">
                        <p className="text-xs sm:text-sm md:text-base font-semibold tracking-wide flex items-center justify-center gap-2 text-emerald-100 uppercase m-0 leading-none">
                            <span className="text-amber-300 font-bold">★</span>
                            <span>Exclusively for <strong className="text-white font-extrabold underline decoration-amber-400 decoration-2 underline-offset-2 px-1">{displayAgencyName}</strong> &amp; travel guest</span>
                            <span className="text-amber-300 font-bold">★</span>
                        </p>
                    </div>

                    {/* Main Voucher Last Page Image with Overlays */}
                    <div className="relative w-full overflow-hidden">
                        <img
                            src={VoucherLastPageImg}
                            alt="Trrip App Digital Companion"
                            className="w-full h-auto block"
                        />

                        {/* Official App Store & Google Play SVG Badge Buttons */}
                        <div
                            data-booking-id={displayBookingId}
                            className="absolute flex flex-col items-start justify-center gap-1.5 z-30 pointer-events-auto"
                            style={{
                                left: '6.2%',
                                top: '75.2%',
                                width: '21.0%',
                                height: '11.5%',
                                boxSizing: 'border-box',
                            }}
                        >
                            <a
                                href={`https://play.google.com/store/apps/details?id=live.trrip.app&hl=en_IN&bookingId=${displayBookingId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Get it on Google Play"
                                className="w-full block hover:opacity-90 transition-opacity cursor-pointer pointer-events-auto relative z-30"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(`https://play.google.com/store/apps/details?id=live.trrip.app&hl=en_IN&bookingId=${displayBookingId}`, '_blank', 'noopener,noreferrer');
                                }}
                            >
                                <img
                                    src={GooglePlayBadge}
                                    alt="Get it on Google Play"
                                    className="w-full h-auto block drop-shadow-sm pointer-events-none"
                                />
                            </a>

                            <a
                                href="https://apps.apple.com/us/app/trrip-your-ontour-assistant/id6759519854"
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Download on the App Store"
                                className="w-full block hover:opacity-90 transition-opacity cursor-pointer pointer-events-auto relative z-30"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    window.open('https://apps.apple.com/us/app/trrip-your-ontour-assistant/id6759519854', '_blank', 'noopener,noreferrer');
                                }}
                            >
                                <img
                                    src={AppStoreBadge}
                                    alt="Download on the App Store"
                                    className="w-full h-auto block drop-shadow-sm pointer-events-none"
                                />
                            </a>
                        </div>

                        {/* Access Code Overlay (Centered inside Amber Yellow Box) */}
                        <div
                            className="absolute flex items-center justify-center pointer-events-none z-10"
                            style={{
                                left: '32.2%',
                                top: '88.2%',
                                width: '21.4%',
                                height: '4.2%',
                            }}
                        >
                            <span className="font-mono font-black text-white tracking-[0.22em] lowercase text-center text-lg sm:text-xl md:text-2xl lg:text-3xl leading-none drop-shadow-md">
                                {displayBookingId}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PDFVoucherView;
